/**
 * @file lib/wellHistory.js
 * @description Well Butler — local history of results produced ON THIS DEVICE.
 *
 * WHY local: the backend persists each sizing/diagnosis/audit row (tenant-scoped)
 * but does NOT yet expose a list/read endpoint (no GET /api/v1/well/history). So
 * the History screen reads this device-local log until that endpoint exists. When
 * the server list endpoint ships, swap History to read it (see report — "needs
 * wiring"). This is intentionally tiny (localStorage, last 50) — not the offline
 * sync queue; it's a convenience recents list, clearly labeled in the UI.
 */

/*****************************************************************
 *  TABLE OF CONTENTS                                             *
 *  SECTION 1 — Where the list lives and how long it is           *
 *  SECTION 2 — Reading the list (shown / hidden)                 *
 *  SECTION 3 — Adding a result                                   *
 *  SECTION 4 — Hiding and bringing back (never deleting)         *
 *****************************************************************/

/*****************************************************************
 *  SECTION 1 — WHERE THE LIST LIVES                              *
 *****************************************************************/

const KEY = 'well.history.v1';   // the browser-storage slot for this device's list
const MAX = 50;                  // keep the newest 50 results on the device

/*****************************************************************
 *  SECTION 2 — READING THE LIST                                  *
 *  Reads: browser storage.   Writes: nothing.                    *
 *****************************************************************/

/* ---- readEveryEntry() ------------------------------------------
   Every saved entry, shown and hidden, newest first. Never throws. */
function readEveryEntry() {
  try {
    const savedText = localStorage.getItem(KEY);
    if (!savedText) {
      return [];
    }
    const savedList = JSON.parse(savedText);
    return Array.isArray(savedList) ? savedList : [];
  } catch (_) {
    return [];
  }
}

/* ---- writeEveryEntry(entries) ----------------------------------
   Saves the whole list back. Best-effort; never throws.           */
function writeEveryEntry(entries) {
  try {
    localStorage.setItem(KEY, JSON.stringify(entries));
  } catch (_) {
    // best-effort only (private window, storage full)
  }
}

/* ---- loadHistory() ---------------------------------------------
   The results the History screen shows: everything not hidden.    */
export function loadHistory() {
  return readEveryEntry().filter((entry) => !entry.hiddenAt);
}

/* ---- countHiddenHistory() --------------------------------------
   How many results are hidden (so the screen can offer them back). */
export function countHiddenHistory() {
  return readEveryEntry().filter((entry) => !!entry.hiddenAt).length;
}

/*****************************************************************
 *  SECTION 3 — ADDING A RESULT                                   *
 *  Writes: browser storage.                                      *
 *****************************************************************/

/**
 * Append a result to the recents list.
 * @param {object} entry { kind:'size'|'diagnose'|'audit'|'megtest'|'trench'|'summarize',
 *                         title, summary, confidence?, persistedId? }
 */
export function pushHistory(entry) {
  const everyEntry = readEveryEntry();
  const randomTag = Math.random().toString(36).slice(2, 8);   // keeps two same-millisecond ids apart
  everyEntry.unshift({
    id: `${Date.now()}-${randomTag}`,
    at: new Date().toISOString(),
    ...entry,
  });
  writeEveryEntry(everyEntry.slice(0, MAX));
}

/*****************************************************************
 *  SECTION 4 — HIDING AND BRINGING BACK                          *
 *  What it does: hides results from the list without deleting    *
 *  them, and brings hidden results back.                         *
 *  Writes: browser storage (a hiddenAt time on each entry).      *
 *****************************************************************/

// WHY (2026-10-07, James): defect #172 — "Clear this device's history" wiped the
// list with no are-you-sure, and the house rule is nothing is ever deleted. Hiding
// stamps each result with the time it was hidden; the result stays on the device
// and "Show hidden results" brings it back.

/* ---- hideAllHistory() ------------------------------------------
   Hides every shown result. Out: how many were hidden.            */
export function hideAllHistory() {
  const hiddenTime = new Date().toISOString();
  let hiddenCount = 0;
  const everyEntry = readEveryEntry().map((entry) => {
    if (entry.hiddenAt) {
      return entry;
    }
    hiddenCount = hiddenCount + 1;
    return { ...entry, hiddenAt: hiddenTime };
  });
  writeEveryEntry(everyEntry);
  return hiddenCount;
}

/* ---- showHiddenHistory() ---------------------------------------
   Brings every hidden result back onto the list.                  */
export function showHiddenHistory() {
  const everyEntry = readEveryEntry().map((entry) => {
    if (!entry.hiddenAt) {
      return entry;
    }
    const shownEntry = { ...entry };   // a copy, so the stored entry is untouched
    delete shownEntry.hiddenAt;        // remove only the hidden stamp
    return shownEntry;
  });
  writeEveryEntry(everyEntry);
}

/* ---- clearHistory() --------------------------------------------
   Kept so older callers keep working; it now HIDES (never wipes). */
export function clearHistory() {
  hideAllHistory();
}
